'use client';

import React, { useEffect, useRef, useState } from 'react';

import css from './index.module.scss';

// Animation constants
const ANIMATION = {
  // Timing
  START_DELAY: 500, // milliseconds before animation begins

  // Particle spawning
  MAX_PARTICLES: 24, // maximum number of particles on screen simultaneously
  SPAWN_RATE: 0.04, // probability (0-1) of spawning a particle each frame

  // Physics
  LIFETIME_FRAMES: 2000, // how many frames a particle lives before being removed
  INITIAL_VELOCITY_Y: 0.4, // base downward velocity in pixels per frame
  VELOCITY_Y_RANGE: 0.8, // random variation added to initial downward velocity
  VELOCITY_X_RANGE: 1.5, // horizontal drift range (-range/2 to +range/2)
  GRAVITY: 0, // acceleration added to downward velocity each frame

  // Visual properties
  ROTATION_SPEED_RANGE: 0.02, // maximum rotation speed in degrees per frame
  SCALE_MIN: 0.7, // minimum size multiplier for particles
  SCALE_RANGE: 1.5, // additional size range (final scale = SCALE_MIN + random * SCALE_RANGE)
};

interface EmojiParticle {
  id: string;
  emoji: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  opacity: number;
  life: number;
  maxLife: number;
}

interface EmojiConfettiProps {
  emojis: string[];
  duration?: number;
  particleCount?: number;
}

export const EmojiConfetti: React.FC<EmojiConfettiProps> = ({ emojis, duration, particleCount = 25 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const [particles, setParticles] = useState<EmojiParticle[]>([]);
  const [isActive, setIsActive] = useState(false);
  const startTimeRef = useRef<number>(0);

  // Start confetti animation shortly after component mounts
  useEffect(() => {
    if (emojis.length > 0) {
      const timer = setTimeout(() => setIsActive(true), ANIMATION.START_DELAY);
      return () => clearTimeout(timer);
    }
  }, [emojis.length]);

  useEffect(() => {
    if (!isActive || emojis.length === 0) {
      setParticles([]);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    startTimeRef.current = Date.now();
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Create initial particles
    const initialParticles: EmojiParticle[] = [];

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTimeRef.current;

      setParticles(prev => {
        let newParticles = [...prev];

        // Add new particles continuously (infinite like iOS)
        if (!duration || elapsed < duration / 2) {
          const shouldAddParticle = Math.random() < ANIMATION.SPAWN_RATE;
          if (shouldAddParticle && newParticles.length < ANIMATION.MAX_PARTICLES) {
            const emoji = emojis[Math.floor(Math.random() * emojis.length)];
            const particle: EmojiParticle = {
              id: `${currentTime}-${Math.random()}`,
              emoji,
              x: Math.random() * containerWidth,
              y: Math.random() * 80, // Spawn in extended top area (0-80px)
              vx: (Math.random() - 0.5) * ANIMATION.VELOCITY_X_RANGE,
              vy: Math.random() * ANIMATION.VELOCITY_Y_RANGE + ANIMATION.INITIAL_VELOCITY_Y,
              rotation: 0,
              rotationSpeed: (Math.random() - 0.5) * ANIMATION.ROTATION_SPEED_RANGE,
              scale: ANIMATION.SCALE_MIN + Math.random() * ANIMATION.SCALE_RANGE,
              opacity: 1,
              life: 0,
              maxLife: ANIMATION.LIFETIME_FRAMES,
            };
            newParticles.push(particle);
          }
        }

        // Update existing particles
        newParticles = newParticles
          .map(particle => ({
            ...particle,
            x: particle.x + particle.vx,
            y: particle.y + particle.vy,
            rotation: particle.rotation + particle.rotationSpeed,
            life: particle.life + 1,
            vy: particle.vy + ANIMATION.GRAVITY,
          }))
          .filter(
            particle =>
              particle.life < particle.maxLife &&
              particle.y < containerHeight && // Extended bottom boundary
              particle.x > -100 &&
              particle.x < containerWidth + 100, // Extended side boundaries
          );

        return newParticles;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, emojis, duration, particleCount]);

  if (emojis.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className={css.container}>
      {particles.map(particle => (
        <div
          key={particle.id}
          className={css.particle}
          style={{
            left: `${particle.x}px`,
            top: `${particle.y}px`,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
            opacity: particle.opacity,
          }}
        >
          {particle.emoji}
        </div>
      ))}
    </div>
  );
};
